'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkClientVersion,
  getVersionState,
  subscribeVersionState,
  type VersionInfo,
  type VersionState,
} from '@/lib/version/client-version';

export {
  assertFreshClientBeforeCriticalAction,
  clearClientRefreshGate,
  canSnoozeClientUpdate,
  checkClientVersion,
  ClientVersionStaleError,
  refreshClientWhenReady,
  getClientBuildHeaders,
  getVersionState,
  isClientUpdateSnoozed,
  isCriticalClientMutation,
  requestClientRefreshGate,
  snoozeClientUpdate,
  subscribeVersionState,
  UPDATE_SNOOZE_MS,
} from '@/lib/version/client-version';
export type { VersionInfo, VersionState } from '@/lib/version/client-version';

const CHECK_INTERVAL = 5 * 60 * 1000;

export function useVersionCheck(onNewVersion?: (info: VersionInfo) => void): VersionState {
  const [state, setState] = useState<VersionState>(() => getVersionState());
  const notifiedBuildRef = useRef<string | null>(null);

  const runCheck = useCallback(() => {
    void checkClientVersion();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeVersionState(setState);
    runCheck();

    const handleFocus = () => runCheck();
    const handleOnline = () => runCheck();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        runCheck();
      }
    };
    const handleVersionStale = () => setState(getVersionState());

    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('dobi:client-version-stale', handleVersionStale);
    const interval = window.setInterval(runCheck, CHECK_INTERVAL);

    return () => {
      unsubscribe();
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('dobi:client-version-stale', handleVersionStale);
      window.clearInterval(interval);
    };
  }, [runCheck]);

  useEffect(() => {
    if (!state.isStale || !state.latestBuildId || !onNewVersion) return;
    if (notifiedBuildRef.current === state.latestBuildId) return;
    notifiedBuildRef.current = state.latestBuildId;
    onNewVersion({
      buildId: state.latestBuildId,
      buildTime: state.latestBuildTime || '',
    });
  }, [onNewVersion, state.isStale, state.latestBuildId, state.latestBuildTime]);

  return state;
}
